import { useEffect, useMemo, useRef, useState } from 'react';
import TopBar from '../components/TopBar';
import {
  fetchCommentImageBlobUrl,
  type CommentDraft,
  useMarkCommentResponseViewed,
  useMyCommentsList,
  useMyDraft,
  usePutDraft,
  useRemoveCommentImage,
  useSubmitDraft,
  useUploadCommentImage,
} from '../api/comments';
import { HttpError } from '../api/http';
import { useAuth } from '../auth/AuthProvider';
import { useAutosave } from '../hooks/useAutosave';
import styles from './CommentsPage.module.css';

const MAX_BYTES = 3 * 1024 * 1024;
const VALID_IMAGE_TYPES = new Set(['image/png', 'image/jpeg']);
const INTRO_COPY =
  'Please leave a comment! Feedback from important users like you is extremely valuable for the continued improvement of the app!';
const RECENT_RESPONSE_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

function blockMessageFromError(err: unknown): string | null {
  if (err instanceof HttpError && err.detailCode === 'ERR_COMMENTER_BLOCKED') {
    return err.detailMessage || 'Comments have been temporarily disabled. Please try again later.';
  }
  return null;
}

function logCommentError(op: string, code: string, err: unknown): void {
  const message = err instanceof Error ? err.message : String(err);
  // eslint-disable-next-line no-console
  console.error('CommentsPage:lastError', { op, code, message, ts: Date.now(), err });
}

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

// User-friendly relative time: "just now", "5 minutes ago", "2 hours ago",
// "yesterday", then a short date ("Jun 18") for anything older.
function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  const then = date.getTime();
  if (!Number.isFinite(then)) return iso;
  const diffMs = Date.now() - then;
  if (diffMs < MINUTE_MS) return 'just now';
  if (diffMs < HOUR_MS) {
    const minutes = Math.floor(diffMs / MINUTE_MS);
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  }
  if (diffMs < DAY_MS) {
    const hours = Math.floor(diffMs / HOUR_MS);
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }
  // Calendar-day comparison so "yesterday" is correct regardless of clock time.
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfThen = new Date(date);
  startOfThen.setHours(0, 0, 0, 0);
  const dayDiff = Math.round((startOfToday.getTime() - startOfThen.getTime()) / DAY_MS);
  if (dayDiff === 1) return 'yesterday';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Most-recent-activity timestamp for sorting: a fresh reply or a fresh
// submission should both float to the top, so take the latest event on the row.
function commentActivityMs(c: CommentDraft): number {
  const iso = c.closed_at ?? c.submitted_at ?? c.updated_at;
  const t = iso ? new Date(iso).getTime() : Number.NaN;
  return Number.isFinite(t) ? t : 0;
}

function isDefaultVisible(c: CommentDraft): boolean {
  if (c.status === 'processing') return true;
  if (c.status !== 'closed' || !c.reply_body) return false;
  if (c.response_viewed_at === null) return true;
  const closedAt = c.closed_at ? new Date(c.closed_at).getTime() : Number.NaN;
  return Number.isFinite(closedAt) && Date.now() - closedAt <= RECENT_RESPONSE_WINDOW_MS;
}

function CommentImage({ filename }: { filename: string }): JSX.Element | null {
  const { getIdToken } = useAuth();
  const [url, setUrl] = useState<string | null>(null);
  const lastUrl = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchCommentImageBlobUrl(filename, getIdToken)
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        if (lastUrl.current) URL.revokeObjectURL(lastUrl.current);
        lastUrl.current = u;
        setUrl(u);
      })
      .catch((err: unknown) => {
        // Thumbnail is additive — render the thread without it, but leave a
        // trail instead of an unhandled rejection.
        logCommentError('thread-image', 'ERR_COMMENT_IMAGE', err);
      });
    return () => {
      cancelled = true;
    };
  }, [filename, getIdToken]);

  useEffect(() => {
    return () => {
      if (lastUrl.current) URL.revokeObjectURL(lastUrl.current);
    };
  }, []);

  if (!url) return null;
  return <img src={url} alt="Attached screenshot" className={styles.thumbnail} />;
}

function CommentThread({ comment }: { comment: CommentDraft }): JSX.Element {
  const markViewed = useMarkCommentResponseViewed();

  useEffect(() => {
    if (comment.id !== null && comment.status === 'closed' && comment.reply_body && !comment.response_viewed_at) {
      markViewed.mutate(comment.id);
    }
    // markViewed is intentionally omitted: the mutation object identity is not
    // part of the visibility decision and including it can retrigger the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comment.id, comment.reply_body, comment.response_viewed_at, comment.status]);

  return (
    <li className={styles.thread} data-testid={`comment-thread-${comment.id ?? 'draft'}`}>
      <div className={styles.threadHeader}>
        <span className={styles.statusBadge} data-status={comment.status}>
          {comment.status === 'processing' ? 'In progress' : 'Reply Received'}
        </span>
        <span className={styles.timestamp}>
          {formatRelativeTime(comment.submitted_at ?? comment.updated_at ?? comment.closed_at)}
        </span>
      </div>
      <div className={styles.echoBlock}>
        <span className={styles.echoLabel}>Your comment</span>
        <p className={styles.echoBody}>{comment.body.trim() === '' ? <em>(no text)</em> : comment.body}</p>
        {comment.image_path && <CommentImage filename={comment.image_path} />}
      </div>
      {comment.status === 'processing' && (
        <p className={styles.statusSubtext}>Comment being processed. Expect a response within 1–72 hours.</p>
      )}
      {comment.status === 'closed' && (
        <div className={styles.replyBlock}>
          <div className={styles.replyHeader}>
            <span className={styles.replyLabel}>Response</span>
            {comment.closed_at && (
              <span className={styles.timestamp}>reply received {formatRelativeTime(comment.closed_at)}</span>
            )}
          </div>
          <p className={styles.replyBody}>{comment.reply_body || <em>(no reply text)</em>}</p>
        </div>
      )}
    </li>
  );
}

export default function CommentsPage(): JSX.Element {
  const { getIdToken } = useAuth();
  const draftQuery = useMyDraft(true);
  const commentsQuery = useMyCommentsList(true);
  const putDraft = usePutDraft();
  const submitDraft = useSubmitDraft();
  const uploadImage = useUploadCommentImage();
  const removeImage = useRemoveCommentImage();

  const data = draftQuery.data;
  const [body, setBody] = useState('');
  const [imagePath, setImagePath] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);
  const [hydratedFromId, setHydratedFromId] = useState<number | null>(null);
  const [showOlder, setShowOlder] = useState(false);
  const lastBlobUrl = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (data && data.id !== hydratedFromId) {
      setBody(data.body ?? '');
      setImagePath(data.image_path ?? null);
      setHydratedFromId(data.id);
    }
  }, [data, hydratedFromId]);

  useEffect(() => {
    let cancelled = false;
    if (lastBlobUrl.current) {
      URL.revokeObjectURL(lastBlobUrl.current);
      lastBlobUrl.current = null;
      setImageUrl(null);
    }
    if (imagePath) {
      fetchCommentImageBlobUrl(imagePath, getIdToken)
        .then((url) => {
          if (cancelled) {
            URL.revokeObjectURL(url);
            return;
          }
          lastBlobUrl.current = url;
          setImageUrl(url);
        })
        .catch((err: unknown) => {
          // Preview is additive — the attach row still shows the remove
          // button; log instead of surfacing an unhandled rejection.
          logCommentError('draft-image', 'ERR_COMMENT_IMAGE', err);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [getIdToken, imagePath]);

  useEffect(() => {
    return () => {
      if (lastBlobUrl.current) URL.revokeObjectURL(lastBlobUrl.current);
    };
  }, []);

  const shouldAutosave =
    draftQuery.isSuccess && (data?.id !== null || body.trim().length > 0 || imagePath !== null);
  const autosaveSave = async ({ body: b }: { body: string }): Promise<unknown> => {
    try {
      return await putDraft.mutateAsync({ kind: null, body: b });
    } catch (err) {
      const msg = blockMessageFromError(err);
      if (msg !== null) {
        setBlockedMessage(msg);
        return undefined;
      }
      logCommentError('autosave', 'ERR_COMMENT_DRAFT', err);
      throw err;
    }
  };
  const { flush } = useAutosave({
    value: { body },
    save: autosaveSave,
    delayMs: 2000,
    enabled: shouldAutosave && blockedMessage === null,
  });

  const visibleComments = useMemo(() => {
    const submitted = (commentsQuery.data ?? [])
      .filter((c) => c.id !== data?.id && c.status !== 'draft')
      .slice()
      .sort((a, b) => commentActivityMs(b) - commentActivityMs(a));
    if (showOlder) return submitted;
    return submitted.filter(isDefaultVisible);
  }, [commentsQuery.data, data?.id, showOlder]);
  const hasOlder = useMemo(() => {
    return (commentsQuery.data ?? []).some((c) => c.id !== data?.id && c.status !== 'draft' && !isDefaultVisible(c));
  }, [commentsQuery.data, data?.id]);

  async function handleSubmit(): Promise<void> {
    setSubmitError(null);
    try {
      await flush();
      await submitDraft.mutateAsync({ kind: null, body });
      setBody('');
      setImagePath(null);
      setImageUrl(null);
      setHydratedFromId(null);
    } catch (err) {
      const msg = blockMessageFromError(err);
      if (msg !== null) {
        setBlockedMessage(msg);
        return;
      }
      // Surface the failure instead of rethrowing into a void onClick —
      // otherwise the button just un-disables with no feedback at all.
      logCommentError('submit', 'ERR_COMMENT_SUBMIT', err);
      setSubmitError(
        'Could not submit your comment — it is saved as a draft, please try again. (ERR_COMMENT_SUBMIT)',
      );
    }
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    setUploadError(null);
    const file = e.target.files?.[0];
    if (!file) return;
    if (!VALID_IMAGE_TYPES.has(file.type)) {
      setUploadError('Please choose a PNG or JPEG image.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > MAX_BYTES) {
      setUploadError(`File too large — max ${(MAX_BYTES / 1024 / 1024).toFixed(0)} MB.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    try {
      const saved = await uploadImage.mutateAsync(file);
      setImagePath(saved.image_path ?? null);
    } catch (err) {
      const blockMsg = blockMessageFromError(err);
      if (blockMsg !== null) {
        setBlockedMessage(blockMsg);
      } else {
        logCommentError('upload', 'ERR_COMMENT_UPLOAD', err);
        setUploadError((err as Error)?.message ?? 'upload failed (ERR_COMMENT_UPLOAD)');
      }
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleRemoveImage(): Promise<void> {
    setUploadError(null);
    try {
      const saved = await removeImage.mutateAsync();
      setImagePath(saved.image_path ?? null);
    } catch (err) {
      setUploadError((err as Error)?.message ?? 'remove failed');
    }
  }

  return (
    <>
      <TopBar />
      <main className={styles.page} data-testid="comments-page">
        <section className={styles.panel}>
          <header className={styles.header}>
            <h1 className={styles.title}>Comments</h1>
            <p className={styles.subtitle}>{INTRO_COPY}</p>
          </header>

          {blockedMessage !== null && (
            <div className={styles.blockedBanner} role="status" data-testid="comments-blocked-banner">
              {blockedMessage}
            </div>
          )}

          <label className={styles.bodyLabel}>
            <span className={styles.legend}>Comment</span>
            <textarea
              className={styles.textarea}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Type your comment…"
              data-testid="comments-body"
              rows={8}
            />
          </label>

          <div className={styles.uploadRow}>
            {!imagePath && (
              <label className={styles.uploadLabel}>
                <span className={styles.legend}>Attach screenshot (optional)</span>
                <span className={styles.uploadHint}>PNG/JPEG only. Max 3 MB.</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={(e) => {
                    void handleFileChange(e);
                  }}
                  className={styles.fileInput}
                  data-testid="comments-file-input"
                />
              </label>
            )}
            {uploadImage.isPending && <span className={styles.uploadStatus}>Uploading…</span>}
            {uploadError && (
              <span className={styles.uploadError} role="alert">
                {uploadError}
              </span>
            )}
            {imagePath && (
              <div className={styles.thumbnailWrap}>
                {imageUrl && (
                  <img
                    src={imageUrl}
                    alt="Attached screenshot"
                    className={styles.thumbnail}
                    data-testid="comments-thumbnail"
                  />
                )}
                <button
                  type="button"
                  onClick={() => {
                    void handleRemoveImage();
                  }}
                  className={styles.secondaryBtn}
                  disabled={removeImage.isPending}
                  data-testid="comments-remove-image"
                >
                  {removeImage.isPending ? 'Removing…' : 'Remove attached image'}
                </button>
              </div>
            )}
          </div>

          <div className={styles.footer}>
            {submitError && (
              <span className={styles.uploadError} role="alert" data-testid="comments-submit-error">
                {submitError}
              </span>
            )}
            <button
              type="button"
              onClick={() => {
                void handleSubmit();
              }}
              className={styles.submit}
              disabled={(body.trim().length === 0 && !imagePath) || submitDraft.isPending || blockedMessage !== null}
              data-testid="comments-submit"
            >
              {submitDraft.isPending ? 'Submitting…' : 'Submit comment'}
            </button>
          </div>
        </section>

        <section className={styles.panel} aria-labelledby="previous-comments-heading">
          <header className={styles.historyHeader}>
            <h2 id="previous-comments-heading" className={styles.historyTitle}>
              Previous comments
            </h2>
            {commentsQuery.isFetching && <span className={styles.loading}>Updating…</span>}
          </header>
          {commentsQuery.isError && (
            <p role="alert" className={styles.error}>
              Could not load comments — {(commentsQuery.error as Error)?.message ?? 'unknown error'}
            </p>
          )}
          {visibleComments.length === 0 && !commentsQuery.isLoading && (
            <p className={styles.empty} data-testid="comments-empty-history">
              No active comments or new replies right now.
            </p>
          )}
          {visibleComments.length > 0 && (
            <ul className={styles.threadList} data-testid="comments-thread-list">
              {visibleComments.map((comment) => (
                <CommentThread key={comment.id} comment={comment} />
              ))}
            </ul>
          )}
          {!showOlder && hasOlder && (
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => setShowOlder(true)}
              data-testid="comments-load-older"
            >
              Load older comments
            </button>
          )}
        </section>
      </main>
    </>
  );
}
